import { api } from "#/lib/api-client";
import type {
  DepartmentOption,
  EmployeeDetails,
  EmployeeFilters,
  EmployeeFormValues,
  EmployeeImportSummary,
  EmployeeRow,
  PageResponse,
  FamilyMember,
  FamilyMemberFormValues,
  GenderOption,
  RelationshipTypeOption,
  RoleOption,
} from "./types";

function cleanText(value?: string | null) {
  const cleaned = value?.trim();
  return cleaned ? cleaned : null;
}

function cleanPayload(values: EmployeeFormValues) {
  const departmentIds = Array.from(
    new Set([
      ...values.departmentIds,

      ...(values.primaryDepartmentId
        ? [Number(values.primaryDepartmentId)]
        : []),
    ]),
  );

  return {
    employeeNumber: values.employeeNumber.trim(),

    userId: values.userId ? Number(values.userId) : null,

    firstName: values.firstName.trim(),

    middleName: cleanText(values.middleName),

    lastName: values.lastName.trim(),

    dateOfBirth: values.dateOfBirth || null,

    gender: cleanText(values.gender),

    maritalStatus: cleanText(values.maritalStatus),

    nationalIdNumber: cleanText(values.nationalIdNumber),

    kraPin: cleanText(values.kraPin),

    nhifNumber: cleanText(values.nhifNumber),

    nssfNumber: cleanText(values.nssfNumber),

    personalEmail: cleanText(values.personalEmail),

    workEmail: cleanText(values.workEmail),

    mobilePhone: cleanText(values.mobilePhone),

    alternativePhone: cleanText(values.alternativePhone),

    addressLine1: cleanText(values.addressLine1),

    addressLine2: cleanText(values.addressLine2),

    cityTown: cleanText(values.cityTown),

    country: cleanText(values.country),

    hireDate: values.hireDate || null,

    probationEndDate: values.probationEndDate || null,

    terminationDate: values.terminationDate || null,

    contractEndDate: values.contractEndDate || null,

    employmentStatus: cleanText(values.employmentStatus),

    employmentType: cleanText(values.employmentType),

    jobTitle: cleanText(values.jobTitle),

    primaryDepartmentId: values.primaryDepartmentId
      ? Number(values.primaryDepartmentId)
      : null,

    departmentIds,

    emergencyContactName: cleanText(values.emergencyContactName),

    emergencyContactRelationship: cleanText(
      values.emergencyContactRelationship,
    ),

    emergencyContactPhone: cleanText(values.emergencyContactPhone),

    systemAccessEnabled: values.systemAccessEnabled,

    businessCardEnabled: values.businessCardEnabled,

    roleCodes: values.roleCodes ?? [],
  };
}

function buildParams(filters: EmployeeFilters = {}) {
  return {
    page: filters.page ?? 0,

    size: filters.size ?? 10,

    search: cleanText(filters.search) ?? undefined,

    status:
      filters.status && filters.status !== "ALL" ? filters.status : undefined,

    employmentType:
      filters.employmentType && filters.employmentType !== "ALL"
        ? filters.employmentType
        : undefined,

    departmentId: filters.departmentId || undefined,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizePage<T>(raw: any): PageResponse<T> {
  const body = raw?.content !== undefined ? raw : (raw?.data ?? raw);
  const meta = body?.page ?? body;
  return {
    content:       body?.content       ?? [],
    totalElements: meta?.totalElements ?? meta?.total_elements ?? meta?.total ?? 0,
    totalPages:    meta?.totalPages    ?? meta?.total_pages    ?? 0,
    number:        meta?.number        ?? 0,
    size:          meta?.size          ?? 0,
    first:         meta?.number === 0,
    last:          (meta?.number ?? 0) + 1 >= (meta?.totalPages ?? meta?.total_pages ?? 1),
  };
}

export async function fetchEmployees(filters: EmployeeFilters = {}) {
  const { data } = await api.get("/api/hr/employees", { params: buildParams(filters) });
  return normalizePage<EmployeeRow>(data);
}

export async function fetchEmployeeById(id: number) {
  const { data } = await api.get<EmployeeDetails>(`/api/hr/employees/${id}`);
  return data;
}

export async function createEmployee(values: EmployeeFormValues) {
  const { data } = await api.post<EmployeeDetails>(
    "/api/hr/employees",
    cleanPayload(values),
  );

  return data;
}

export async function updateEmployee(id: number, values: EmployeeFormValues) {
  const { data } = await api.put<EmployeeDetails>(
    `/api/hr/employees/${id}`,
    cleanPayload(values),
  );

  return data;
}

export async function deleteEmployee(id: number) {
  await api.delete(`/api/hr/employees/${id}`);
}

/** Blocks login (disables the linked account) and marks the employee SUSPENDED. */
export async function deactivateEmployee(id: number) {
  const { data } = await api.put<EmployeeDetails>(`/api/hr/employees/${id}/deactivate`);
  return data;
}

/** Restores login access and marks the employee ACTIVE. */
export async function activateEmployee(id: number) {
  const { data } = await api.put<EmployeeDetails>(`/api/hr/employees/${id}/activate`);
  return data;
}

export async function fetchDepartmentOptions() {
  const { data } = await api.get<DepartmentOption[]>(
    "/api/hr/departments/options",
  );

  return data;
}

export async function fetchEmployeeOptions() {
  const { data } = await api.get<EmployeeRow[]>("/api/hr/employees/options");
  return data;
}

export async function importEmployees(file: File) {
  const formData = new FormData();
  formData.append("file", file);

  const { data } = await api.post<EmployeeImportSummary>(
    "/api/hr/employees/import",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );

  return data;
}

export async function downloadImportTemplate(): Promise<Blob> {
  const { data } = await api.get("/api/hr/employees/import/template", {
    responseType: "blob",
  });
  return data;
}

/** Trigger a browser download for an already-fetched Blob via a short-lived in-memory URL. */
export function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export function employeeVCardUrl(employeeId: number) {
  const baseUrl = api.defaults.baseURL ?? "";
  return `${baseUrl}/api/hr/employees/${employeeId}/vcard`;
}

export function employeePublicProfileUrl(token: string) {
  return `${window.location.origin}/public/employee/${token}`;
}
function cleanFamilyPayload(values: FamilyMemberFormValues) {
  return {
    relationshipTypeId: values.relationshipTypeId
      ? Number(values.relationshipTypeId)
      : null,
    fullName: values.fullName.trim(),
    dateOfBirth: values.dateOfBirth || null,
    genderId: values.genderId ? Number(values.genderId) : null,
    dependent: values.dependent,
    nextOfKin: values.nextOfKin,
    notes: cleanText(values.notes),
  };
}

export async function fetchRoleOptions() {

  const { data } = await api.get<RoleOption[]>("/api/hr/roles/options");

  return data;

}

export async function fetchRelationshipTypes() {
  const { data } = await api.get<RelationshipTypeOption[]>(
    "/api/hr/ref/relationship-types",
  );
  return data;
}

export async function fetchGenderOptions() {
  const { data } = await api.get<GenderOption[]>("/api/hr/ref/lookups", {
    params: { lookupType: "GENDER", activeOnly: true },
  });
  return data;
}

export async function fetchEmployeeFamily(employeeId: number) {
  const { data } = await api.get<FamilyMember[]>(
    `/api/hr/employees/${employeeId}/family`,
  );
  return data;
}

export async function createFamilyMember(
  employeeId: number,
  values: FamilyMemberFormValues,
) {
  const { data } = await api.post<FamilyMember>(
    `/api/hr/employees/${employeeId}/family`,
    cleanFamilyPayload(values),
  );
  return data;
}

export async function updateFamilyMember(
  employeeId: number,
  familyId: number,
  values: FamilyMemberFormValues,
) {
  const { data } = await api.put<FamilyMember>(
    `/api/hr/employees/${employeeId}/family/${familyId}`,
    cleanFamilyPayload(values),
  );
  return data;
}

export async function deleteFamilyMember(employeeId: number, familyId: number) {
  await api.delete(`/api/hr/employees/${employeeId}/family/${familyId}`);
}